import {Component, Injector, OnDestroy} from '@angular/core';
import {BaseAdminPanelComponent} from "../../base-admin-panel/base-admin-panel.component";
import {Observable, Subject, takeUntil} from "rxjs";
import { ReportTemplateViewModel } from '../Models/ReportTemplateViewModel';
import { CreateReportTemplateComponent } from '../create-report-template.component';
import { EditReportTemplateComponent } from '../edit-report-template.component';
import { ReportTemplateService } from '../../../../services/reporttemplate.service';
import { ContainersService } from '../../../../services/containers.service';
import { ContainerViewModel } from '../../ContainerViewModel';
import { ContainerAcl } from 'app/models/ContainerAcl';
import { matchesAnySearchRule } from "../../../shared/pipes/filterByProperty";

@Component({
    selector: '[app-report-templates]',
    templateUrl: './report-templates.component.html',
    styleUrls: ['./report-templates.component.css'],
    standalone: false
})
export class ReportTemplatesComponent extends BaseAdminPanelComponent implements OnDestroy {

  protected reportService: ReportTemplateService;
  protected containersService: ContainersService;
  public reportTypes = new Map<number, string>();
  public containerFilterId: number = 0;
  public containers: ContainerViewModel[] = [];
  private containerAclById = new Map<number, number>();
  private containerNameById = new Map<number, string>();
  private readonly destroy$ = new Subject<void>();

  constructor(protected injector: Injector)
  {
    super(injector);
    this.reportService = injector.get(ReportTemplateService);
    this.containersService = injector.get(ContainersService);
    this.entityName = "Report templates";

    this.reportService.getReportTypes()
      .subscribe(types => { this.reportTypes = types; });
  }

  protected Refresh() {
    this.loadContainers();

    this.reportService.getReportTemplates()
      .subscribe(reports => {
        this.objects = reports;
    });
  }

  override ngOnInit() {
    super.ngOnInit();

    this.usersService.onInit.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.buildContainerAclMap();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get filteredReports(): ReportTemplateViewModel[] {
    let items = this.objects as ReportTemplateViewModel[];

    const term = this.filter.term ?? "";
    if (term) {
      items = items.filter(report =>
        matchesAnySearchRule(term, report.Name, this.reportTypes.get(report.Type) ?? ""));
    }

    if (this.containerFilterId > 0)
      items = items.filter(report => report.ContainerId === this.containerFilterId);

    return items;
  }

  public getContainerName(containerId?: number): string {
    if (!containerId)
      return "";

    return this.containerNameById.get(containerId) ?? "";
  }


  protected doDeleteObject(id: number): Observable<any>
  {
    return this.reportService.delete(id);
  }

  public openCreateModal() {
    if (!this.canAddReportTemplate()) return;

    this.prepareModal(CreateReportTemplateComponent);
    this.componentRef.instance.open(new ReportTemplateViewModel());
  }

  public openEditModal(command: ReportTemplateViewModel) {
    this.prepareModal(EditReportTemplateComponent);
    this.componentRef.instance.open(command);
  }

  public onEditReportTemplate(report: ReportTemplateViewModel, event?: Event) {
    event?.stopPropagation();
    if (!this.canEditReportTemplate(report)) return;
    this.openEditModal(report);
  }

  public onDeleteReportTemplate(report: ReportTemplateViewModel, event?: Event) {
    event?.stopPropagation();
    if (!this.canDeleteReportTemplate(report)) return;
    this.deleteObject(report.Id);
  }

  public canAddReportTemplate(): boolean {
    const user = this.usersService.getCurrentUser();
    if (!user) return false;

    if (user.CanDo(this.AccessRights.EDITUSERS) || user.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return true;

    for (const acl of this.containerAclById.values()) {
      if ((acl & ContainerAcl.CreateReportTemplate) === ContainerAcl.CreateReportTemplate)
        return true;
    }

    return false;
  }

  public canEditReportTemplate(report: ReportTemplateViewModel): boolean {
    return this.hasReportTemplatePermission(report, ContainerAcl.EditReportTemplate);
  }

  public canDeleteReportTemplate(report: ReportTemplateViewModel): boolean {
    return this.hasReportTemplatePermission(report, ContainerAcl.DeleteReportTemplate);
  }

  private hasReportTemplatePermission(report: ReportTemplateViewModel, permission: ContainerAcl): boolean {
    const user = this.usersService.getCurrentUser();
    if (!user) return false;

    if (user.CanDo(this.AccessRights.EDITUSERS) || user.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return true;

    if (!report?.ContainerId)
      return false;

    const acl = this.containerAclById.get(report.ContainerId) ?? 0;
    return (acl & permission) === permission;
  }

  private loadContainers() {
    const currentUser = this.usersService.getCurrentUser();
    if (!currentUser) {
      this.containers = [];
      this.containerAclById = new Map<number, number>();
      this.containerNameById = new Map<number, string>();
      this.containerFilterId = 0;
      return;
    }

    this.containersService.GetContainers().subscribe(containers => {
      this.containers = [...(containers ?? [])]
        .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
      this.containerNameById = new Map(this.containers.map(container => [container.Id, container.Name] as [number, string]));
      if (this.containerFilterId > 0 && !this.containers.some(container => container.Id === this.containerFilterId))
        this.containerFilterId = 0;
      this.buildContainerAclMap();
    });
  }

  private buildContainerAclMap() {
    this.containerAclById = new Map<number, number>();

    for (const container of this.containers) {
      this.containerAclById.set(container.Id, this.getContainerAcl(container));
    }
  }

  private getContainerAcl(container: ContainerViewModel): number {
    const currentUser = this.usersService.getCurrentUser();
    if (!currentUser)
      return 0;

    if (currentUser.CanDo(this.AccessRights.EDITUSERS) || currentUser.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return ContainerAcl.CreateReportTemplate | ContainerAcl.EditReportTemplate | ContainerAcl.DeleteReportTemplate;

    const userId = currentUser.Id;
    const aclFromUsers = container.Users?.find(link => link.UserId === userId)?.ACL;
    const fallbackAcl = (currentUser.ContainerLinks ?? []).find(link => link.ContainerId === container.Id)?.ACL;
    return aclFromUsers ?? fallbackAcl ?? 0;
  }
}
